import { FC, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Radio, Svg, Typography, useThemeValue } from '@altermaind/web/ui';
import { useDialog } from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { getUserProfileCall, postContactCall } from '@/core/state/api';
import { ContactMeFields } from './ContactMeFields';
import { ContactOtherFields } from './ContactOtherFields';
import {
  ContactMode,
  DialogContactsFeedbackPayload,
  DialogContactsFormValues,
  DialogContactsPayload,
} from './types';

export type { DialogContactsPayload } from './types';

const DEFAULT_MODE: ContactMode = 'contactMe';

type ModeItem = {
  value: ContactMode;
  label: string;
  description: string;
  iconName: 'user' | 'userPlus';
};

export const DialogContacts: FC = () => {
  const t = useTranslations('core.dialogContacts');
  const { closeDialog, Dialog, isOpen, payload } = useDialog<DialogContactsPayload>('contacts');
  const { openDialog: openFeedbackDialog } =
    useDialog<DialogContactsFeedbackPayload>('contactsFeedback');

  const {
    firstName: userFirstName,
    lastName: userLastName,
    email: userEmail,
  } = getUserProfileCall.hook();
  const { execute: executePostContact, loading: loadingPostContact } = postContactCall.hook();

  const { isMobile } = useBreakingPoints();

  const gap = parseFloat(`${useThemeValue('sizes', 'xs')}`);

  const defaultValues = useMemo<DialogContactsFormValues>(
    () => ({
      mode: DEFAULT_MODE,
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
    }),
    [],
  );

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { isValid, errors: formErrors },
  } = useForm<DialogContactsFormValues>({
    defaultValues,
    mode: 'onBlur',
  });

  const mode = watch('mode');

  const modeItems = useMemo<ModeItem[]>(
    () => [
      {
        value: 'contactMe',
        label: t('modes.contactMe.label'),
        description: t('modes.contactMe.description'),
        iconName: 'user',
      },
      {
        value: 'contactOther',
        label: t('modes.contactOther.label'),
        description: t('modes.contactOther.description'),
        iconName: 'userPlus',
      },
    ],
    [t],
  );

  const handleClose = () => {
    reset(defaultValues);
    closeDialog();
  };

  const handleSelectMode = (value: ContactMode) => {
    setValue('mode', value, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  const onSubmit = (values: DialogContactsFormValues) => {
    const messageBody = values.message?.trim() || undefined;
    const isContactMe = values.mode === 'contactMe';

    executePostContact({
      message: messageBody,
      contact: isContactMe
        ? {
            firstName: userFirstName ?? '',
            lastName: userLastName ?? '',
            email: userEmail || undefined,
          }
        : {
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email || undefined,
            phone: values.phone || undefined,
          },
      successSaga: () => {
        reset(defaultValues);
        closeDialog();
        openFeedbackDialog({ status: 'success' });
      },
      errorSaga: () => {
        closeDialog();
        openFeedbackDialog({ status: 'error', formValues: values });
      },
    });
  };

  const isContactMe = mode === 'contactMe';

  useEffect(() => {
    if (isOpen) {
      reset(payload?.initialValues ?? defaultValues);
    }
  }, [isOpen, defaultValues, payload, reset]);

  return (
    <Dialog
      maxWidth='900px'
      open={isOpen}
      title={undefined}
      onClose={handleClose}
      closeButton
      dialogStyle={{ style: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Box display='flex' flexDirection='column'>
        <Typography mt='none' variant='h3' color='primary/text' textAlign='center'>
          {t('title')}
        </Typography>
      </Box>

      <Box mt='m' display='flex' flexDirection='column' style={{ gap }}>
        <Typography variant='p2/light' textAlign='center' color='grey/light'>
          {t('description')}
        </Typography>

        <Box mt='s' display='flex' flexDirection={isMobile ? 'column' : 'row'} style={{ gap }}>
          {modeItems.map(item => {
            const isSelected = mode === item.value;
            return (
              <Box
                key={item.value}
                flex={1}
                display='flex'
                flexDirection='row'
                alignItems='flex-start'
                p='s'
                border='1px solid'
                borderRadius='m'
                borderColor={isSelected ? 'primary' : 'grey/light/background'}
                onClick={() => handleSelectMode(item.value)}
                style={{ cursor: 'pointer', gap }}
                data-testid={`dialog-contacts-mode-${item.value}`}
              >
                <Box>
                  <Radio checked={isSelected} />
                </Box>
                <Box display='flex' flexDirection='column' flex={1}>
                  <Box display='flex'>
                    <Svg name={item.iconName} size='s' />
                    <Typography variant='p2/bold' color='primary/text' ml='xxs'>
                      {item.label}
                    </Typography>
                  </Box>
                  <Typography variant='p3/light' color='grey/light' mt='xxs'>
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {isContactMe ? (
          <ContactMeFields
            control={control}
            gap={gap}
            firstName={userFirstName}
            lastName={userLastName}
            email={userEmail}
          />
        ) : (
          <ContactOtherFields control={control} gap={gap} errors={formErrors} />
        )}

        <Box mt='l' display='flex' flexDirection='row' justifyContent='flex-end'>
          <Button
            variant='primary'
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || loadingPostContact}
            spinnerColor='white'
          >
            {t('buttons.send')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
